import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import BlockIcon              from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";
import { POPPINS, HEADER_COLOR, fmtShort } from "./adminConstants";

const METRIC_COLS = [
  { id: "full_name",             label: "User",          sortable: false, width: 190, align: "left"   },
  { id: "is_active",             label: "Status",        sortable: true,  width: 90,  align: "left"   },
  { id: "created_at",            label: "Created",       sortable: true,  width: 105, align: "left"   },
  { id: "last_login",            label: "Last Login",    sortable: true,  width: 108, align: "left"   },
  { id: "days_since_last_login", label: "Inactive Days", sortable: true,  width: 108, align: "center" },
  { id: "total_logins",          label: "Logins",        sortable: true,  width: 72,  align: "center" },
  { id: "failed_logins",         label: "Failed",        sortable: true,  width: 68,  align: "center" },
  { id: "total_groups",          label: "Groups",        sortable: true,  width: 72,  align: "center" },
  { id: "total_messages",        label: "Chat Count",    sortable: true,  width: 85,  align: "center" },
  { id: "last_chat",             label: "Last Chat",     sortable: true,  width: 108, align: "left"   },
];

const rowBg = (r) => {
  if (r.soft_delete) return "#f5f5f5";
  if (!r.last_login) return "#fce4ec";
  const d = r.days_since_last_login ?? 0;
  if (d > 60) return "#fce4ec";
  if (d > 30) return "#fff8e1";
  return "transparent";
};

const UserMetricsTable = ({
  rows,
  total,
  loading,
  page,
  rpp,
  sortCol,
  sortDir,
  onSort,
  onPage,
  onRpp,
  onLoginDrawerOpen,
  onChatDrawerOpen,
}) => (
  <>
    {/* Row-colour legend */}
    <Box sx={{ display: "flex", gap: 2, px: 3, pb: 1, alignItems: "center" }}>
      <Typography variant="caption" sx={{ ...POPPINS, color: "#999", fontSize: "0.69rem" }}>Row highlight:</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: "#fce4ec", border: "1px solid #ef9a9a" }} />
        <Typography variant="caption" sx={{ ...POPPINS, color: "#888", fontSize: "0.69rem" }}>
          Inactive &gt; 60 days / never logged in
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: "#fff8e1", border: "1px solid #ffe082" }} />
        <Typography variant="caption" sx={{ ...POPPINS, color: "#888", fontSize: "0.69rem" }}>Inactive 31–60 days</Typography>
      </Box>
    </Box>

    {/* Table */}
    <TableContainer sx={{ maxHeight: "calc(100vh - 450px)" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {METRIC_COLS.map((col) => (
              <TableCell
                key={col.id}
                align={col.align}
                sortDirection={sortCol === col.id ? sortDir : false}
                sx={{
                  ...POPPINS, fontWeight: 700, fontSize: "0.73rem",
                  color: HEADER_COLOR, backgroundColor: "#f5f7fb",
                  whiteSpace: "nowrap", minWidth: col.width, py: 1,
                }}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortCol === col.id}
                    direction={sortCol === col.id ? sortDir : "asc"}
                    onClick={() => onSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={METRIC_COLS.length} align="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={METRIC_COLS.length} align="center" sx={{ py: 5 }}>
                <Typography variant="body2" color="text.secondary" sx={POPPINS}>No users found.</Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((r) => (
            <TableRow
              key={r.user_id}
              hover
              sx={{ backgroundColor: rowBg(r), "&:last-child td": { border: 0 }, "&:hover": { filter: "brightness(0.97)" } }}
            >
              {/* User */}
              <TableCell sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ ...POPPINS, fontWeight: 600, fontSize: "0.81rem" }}>
                  {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                </Typography>
                <Typography variant="caption" sx={{ ...POPPINS, color: "#777", fontSize: "0.71rem" }}>
                  {r.email_id}
                </Typography>
              </TableCell>

              {/* Status */}
              <TableCell>
                {r.soft_delete ? (
                  <Chip icon={<BlockIcon sx={{ fontSize: "12px !important" }} />} label="Deleted"  size="small" color="error"   variant="outlined" sx={{ ...POPPINS, fontSize: "0.64rem" }} />
                ) : r.is_active ? (
                  <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: "12px !important" }} />} label="Active"   size="small" color="success" variant="filled"   sx={{ ...POPPINS, fontSize: "0.64rem" }} />
                ) : (
                  <Chip icon={<ErrorOutlineIcon sx={{ fontSize: "12px !important" }} />} label="Inactive" size="small" color="warning" variant="outlined" sx={{ ...POPPINS, fontSize: "0.64rem" }} />
                )}
              </TableCell>

              {/* Created */}
              <TableCell sx={{ ...POPPINS, fontSize: "0.75rem", color: "#666", whiteSpace: "nowrap" }}>
                {fmtShort(r.created_at)}
              </TableCell>

              {/* Last Login */}
              <TableCell sx={{ ...POPPINS, fontSize: "0.75rem", color: "#555", whiteSpace: "nowrap" }}>
                {r.last_login ? fmtShort(r.last_login) : (
                  <Typography component="span" sx={{ ...POPPINS, fontSize: "0.72rem", color: "#d32f2f", fontStyle: "italic" }}>Never</Typography>
                )}
              </TableCell>

              {/* Inactive Days */}
              <TableCell align="center">
                {r.last_login ? (
                  <Tooltip title={`Last login: ${fmtShort(r.last_login)}`}>
                    <Chip
                      label={`${r.days_since_last_login ?? 0}d`}
                      size="small"
                      sx={{
                        ...POPPINS, fontSize: "0.68rem", fontWeight: 600,
                        backgroundColor: (r.days_since_last_login ?? 0) > 60 ? "#ffcdd2" :
                                         (r.days_since_last_login ?? 0) > 30 ? "#fff9c4" : "#e8f5e9",
                        color:           (r.days_since_last_login ?? 0) > 60 ? "#c62828" :
                                         (r.days_since_last_login ?? 0) > 30 ? "#f57f17" : "#2e7d32",
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Typography component="span" sx={{ ...POPPINS, fontSize: "0.72rem", color: "#d32f2f" }}>—</Typography>
                )}
              </TableCell>

              {/* Logins — clickable */}
              <TableCell align="center">
                <Tooltip title={`View login history for ${r.email_id}`}>
                  <Typography
                    component="span"
                    onClick={() => onLoginDrawerOpen(r)}
                    sx={{
                      ...POPPINS, fontSize: "0.8rem", fontWeight: 700,
                      color: (r.total_logins ?? 0) > 0 ? "#1976d2" : "#bbb",
                      cursor: (r.total_logins ?? 0) > 0 ? "pointer" : "default",
                      textDecoration: (r.total_logins ?? 0) > 0 ? "underline" : "none",
                      "&:hover": { color: (r.total_logins ?? 0) > 0 ? "#1565c0" : "#bbb" },
                    }}
                  >
                    {r.total_logins ?? 0}
                  </Typography>
                </Tooltip>
              </TableCell>

              {/* Failed Logins */}
              <TableCell align="center">
                {(r.failed_logins ?? 0) > 0 ? (
                  <Chip label={r.failed_logins} size="small" color="error" variant="outlined" sx={{ ...POPPINS, fontSize: "0.68rem", fontWeight: 600 }} />
                ) : (
                  <Typography component="span" sx={{ ...POPPINS, fontSize: "0.8rem", color: "#bbb" }}>0</Typography>
                )}
              </TableCell>

              {/* Groups */}
              <TableCell align="center" sx={{ ...POPPINS, fontSize: "0.8rem" }}>
                {r.total_groups ?? 0}
              </TableCell>

              {/* Chat Count — clickable */}
              <TableCell align="center">
                <Tooltip title={`View chat history for ${r.email_id}`}>
                  <Typography
                    component="span"
                    onClick={() => (r.total_messages ?? 0) > 0 && onChatDrawerOpen(r)}
                    sx={{
                      ...POPPINS, fontSize: "0.8rem", fontWeight: 500,
                      color: (r.total_messages ?? 0) > 0 ? "#1976d2" : "#bbb",
                      cursor: (r.total_messages ?? 0) > 0 ? "pointer" : "default",
                      textDecoration: (r.total_messages ?? 0) > 0 ? "underline" : "none",
                      "&:hover": { color: (r.total_messages ?? 0) > 0 ? "#1565c0" : "#bbb" },
                    }}
                  >
                    {r.total_messages ?? 0}
                  </Typography>
                </Tooltip>
              </TableCell>

              {/* Last Chat */}
              <TableCell sx={{ ...POPPINS, fontSize: "0.75rem", color: "#555", whiteSpace: "nowrap" }}>
                {fmtShort(r.last_chat)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Pagination */}
    <Box sx={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #e0e0e0" }}>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={onPage}
        rowsPerPage={rpp}
        onRowsPerPageChange={onRpp}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ ...POPPINS, fontSize: "0.78rem" }}
      />
    </Box>
  </>
);

export default UserMetricsTable;
